import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    TextField,
    Button,
    Link,
    Alert,
    useTheme,
} from '@mui/material';
import {
    ChatBubble,
    Schedule,
    Email,
    Article,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const SupportPage: React.FC = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [isBusinessHours, setIsBusinessHours] = useState(false);
    const [currentTime, setCurrentTime] = useState('');
    const [email, setEmail] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    const [message, setMessage] = useState('');
    const [submitted, setSubmitted] = useState(false);

    // Check if current time is within business hours (Brasilia timezone)
    const checkBusinessHours = () => {
        const now = new Date();

        // Convert to Brasilia timezone (America/Sao_Paulo)
        const brasiliaTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
        const hours = brasiliaTime.getHours();
        const minutes = brasiliaTime.getMinutes();
        const timeInMinutes = hours * 60 + minutes;

        // Business hours: 9:00-12:00 and 13:00-18:00
        const morningStart = 9 * 60; // 9:00
        const morningEnd = 12 * 60; // 12:00
        const afternoonStart = 13 * 60; // 13:00
        const afternoonEnd = 18 * 60; // 18:00

        const isWithinHours = (timeInMinutes >= morningStart && timeInMinutes < morningEnd) ||
            (timeInMinutes >= afternoonStart && timeInMinutes < afternoonEnd);

        setIsBusinessHours(isWithinHours);
        setCurrentTime(brasiliaTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }));
    };

    useEffect(() => {
        checkBusinessHours();
        // Update every minute
        const interval = setInterval(checkBusinessHours, 60000);
        return () => clearInterval(interval);
    }, []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Here you would typically send the data to your backend
        console.log('Form submitted:', { email, whatsapp, message });
        setSubmitted(true);
        // Reset form after 3 seconds
        setTimeout(() => {
            setSubmitted(false);
            setEmail('');
            setWhatsapp('');
            setMessage('');
        }, 3000);
    };

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ color: theme.palette.text.primary }}>
                    {t('Support')}
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                    {t('We are here to help you with any questions or issues.')}
                </Typography>
            </Box>

            {/* Business Hours Status */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 3,
                    backgroundColor: isBusinessHours
                        ? theme.palette.success.main + '15'
                        : theme.palette.warning.main + '15',
                    borderLeft: `4px solid ${isBusinessHours ? theme.palette.success.main : theme.palette.warning.main}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Schedule sx={{ mr: 1, color: isBusinessHours ? theme.palette.success.main : theme.palette.warning.main }} />
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        {t('Real-time Support Availability')}
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                    {t('Current time (Brasilia):')} <strong>{currentTime}</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                    {t('Business hours:')} <strong>9:00 - 12:00</strong> {t('and')} <strong>13:00 - 18:00</strong>
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary, mt: 2, fontWeight: 500 }}>
                    {isBusinessHours
                        ? t('✓ Chat support is currently available!')
                        : t('Chat support is currently unavailable. Please use the contact form below.')}
                </Typography>
            </Paper>

            {/* Live Chat Section (visible during business hours) */}
            {isBusinessHours && (
                <Paper
                    elevation={2}
                    sx={{
                        p: 3,
                        mb: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.dark}15 100%)`,
                        borderRadius: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ChatBubble sx={{ mr: 1, color: theme.palette.primary.main }} />
                        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                            {t('Live Chat Support')}
                        </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                        {t('Our support team is available now! Click the button below to start a chat conversation.')}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={<ChatBubble />}
                        sx={{ mt: 1 }}
                        component="a"
                        href="https://wa.me/5511930891173"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('Start Live Chat')}
                    </Button>
                </Paper>
            )}

            {/* Contact Form (always visible, but emphasized outside business hours) */}
            {!isBusinessHours && (
                <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Email sx={{ mr: 1, color: theme.palette.primary.main }} />
                        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                            {t('Contact Us')}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                        {t('Send us a message and we will get back to you on the following business day.')}
                    </Typography>

                    {submitted && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {t('Thank you! We will contact you soon.')}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label={t('Email')}
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            sx={{ mb: 2 }}
                            placeholder="contato@live4.tv"
                        />
                        <TextField
                            fullWidth
                            label={t('WhatsApp Number')}
                            type="tel"
                            value={whatsapp}
                            onChange={(e) => setWhatsapp(e.target.value)}
                            sx={{ mb: 2 }}
                            placeholder="+55 11 99999-9999"
                            helperText={t('Provide your WhatsApp so we can contact you quickly')}
                        />
                        <TextField
                            fullWidth
                            label={t('Message')}
                            multiline
                            rows={4}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            required
                            sx={{ mb: 2 }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            size="large"
                            fullWidth
                            disabled={submitted}
                        >
                            {t('Send Message')}
                        </Button>
                    </form>

                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 2, textAlign: 'center' }}>
                        {t('Or email us directly at:')} <Link href="mailto:contato@live4.tv" sx={{ color: theme.palette.primary.main }}>contato@live4.tv</Link>
                    </Typography>
                </Paper>
            )}

            {/* Blog Link */}
            <Paper
                elevation={1}
                sx={{
                    p: 3,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.info.main}15 0%, ${theme.palette.info.dark}15 100%)`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Article sx={{ mr: 1, color: theme.palette.info.main }} />
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        {t('Knowledge Base')}
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                    {t('Find answers to common questions and learn more about our platform in our blog.')}
                </Typography>
                <Button
                    variant="outlined"
                    color="info"
                    href="https://www.live4.tv/blog"
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<Article />}
                >
                    {t('Visit Our Blog')}
                </Button>
            </Paper>
        </Container>
    );
};

export default SupportPage;
